import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TenantUserResponse } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

export type UserStatusFilter = 'active' | 'deactivated' | 'all';

export interface UserFilters {
  /** Filter by role id (null = all roles). */
  roleId: string | null;
  status: UserStatusFilter;
  search: string;
  /** Include users across the whole tenant subtree (parent-org aggregate view). */
  includeDescendants?: boolean;
}

export interface CreateUserBody {
  email: string;
  role_id: string;
  display_name?: string | undefined;
}

export interface UpdateUserBody {
  role_id?: string | undefined;
  display_name?: string | undefined;
}

export function useUsers({ enabled = true, pollInterval = 30_000 }: { enabled?: boolean; pollInterval?: number } = {}) {
  const apiClient = useApiClient();
  const [users, setUsers] = useState<TenantUserResponse[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [filters, setFiltersState] = useState<UserFilters>({ roleId: null, status: 'active', search: '' });
  const filtersRef = useRef(filters);
  const inFlightRef = useRef(false);
  const loadedRef = useRef(false);

  const fetch = useCallback(() => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const f = filtersRef.current;
    const query: Record<string, string> = {};
    if (f.status !== 'all') query.status = f.status;
    if (f.roleId) query.role_id = f.roleId;
    if (f.includeDescendants) query.include_descendants = 'true';
    apiClient
      .listUsers(Object.keys(query).length > 0 ? query : undefined)
      .then((res) => {
        setUsers(res.data);
        loadedRef.current = true;
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
        if (!loadedRef.current) setStatus('error');
      })
      .finally(() => {
        inFlightRef.current = false;
      });
  }, [apiClient]);

  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;

  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    fetchRef.current();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchRef.current();
    }, pollInterval);
    return () => clearInterval(id);
  }, [enabled, pollInterval]);

  const setFilters = useCallback((f: UserFilters) => {
    filtersRef.current = f;
    setFiltersState(f);
    if (loadedRef.current) {
      inFlightRef.current = false;
      fetchRef.current();
    }
  }, []);

  const filtered = useMemo(() => {
    let result = [...users];
    const f = filters;
    if (f.search) {
      const q = f.search.toLowerCase();
      result = result.filter(
        (u) => u.email.toLowerCase().includes(q) || u.display_name?.toLowerCase().includes(q),
      );
    }
    result.sort((a, b) => b.created_at.localeCompare(a.created_at));
    return result;
  }, [users, filters]);

  // Counts keyed by role slug (dynamic — roles are data, not a fixed enum).
  const roleCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const u of users) {
      const slug = u.role_slug ?? 'unknown';
      c[slug] = (c[slug] ?? 0) + 1;
    }
    return c;
  }, [users]);

  const createUser = useCallback(
    async (body: CreateUserBody) => {
      const created = await apiClient.createUser(body);
      setUsers((prev) => [created, ...prev]);
      return created;
    },
    [apiClient],
  );

  const updateUser = useCallback(
    async (id: string, body: UpdateUserBody) => {
      const updated = await apiClient.updateUser(id, body);
      setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
      return updated;
    },
    [apiClient],
  );

  const deactivateUser = useCallback(
    async (id: string) => {
      const deactivated = await apiClient.deactivateUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      return deactivated;
    },
    [apiClient],
  );

  const reload = useCallback(() => {
    if (loadedRef.current) {
      inFlightRef.current = false;
      fetchRef.current();
    }
  }, []);

  return {
    users: filtered,
    allUsers: users,
    status,
    error,
    filters,
    setFilters,
    roleCounts,
    createUser,
    updateUser,
    deactivateUser,
    reload,
  };
}
